import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Store } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { listTiposLocal, listUsosLocal } from "@/lib/datos.functions";
import { searchRank } from "@/lib/search-text";

const ONE_DAY = 24 * 60 * 60 * 1000;

export type TipoLocalSuggestion =
  | { kind: "tipo"; id: string; slug: string; nombre: string }
  | { kind: "uso"; id: string; slug: string; nombre: string; tipoLocalId?: string; tipoLocalNombre?: string };

export function TipoLocalCombobox({
  value,
  onChange,
  placeholder = "Elige el tipo de local",
  clearInputOnSelect = false,
}: {
  value: TipoLocalSuggestion | null;
  onChange: (s: TipoLocalSuggestion) => void;
  placeholder?: string;
  clearInputOnSelect?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const triggerRef = useRef<HTMLButtonElement>(null);

  const tiposUsosQuery = useQuery({
    queryKey: ["search-tipos-usos"],
    enabled: open,
    staleTime: ONE_DAY,
    queryFn: async () => {
      const [t, u] = await Promise.all([listTiposLocal(), listUsosLocal()]);
      return {
        tipos: (t ?? []).map((x: any) => ({ id: x.id, nombre: x.nombre, slug: x.slug })),
        usos: (u ?? []).map((x: any) => ({
          id: x.id,
          nombre: x.nombre,
          slug: x.slug,
          tipos_local: x.tipos_local,
        })),
      };
    },
  });
  const tipos = tiposUsosQuery.data?.tipos ?? [];
  const usos = tiposUsosQuery.data?.usos ?? [];

  const all: TipoLocalSuggestion[] = [
    ...tipos.map((t) => ({ kind: "tipo" as const, ...t })),
    ...usos.map((u) => ({
      kind: "uso" as const,
      id: u.id,
      slug: u.slug,
      nombre: u.nombre,
      tipoLocalId: u.tipos_local?.id,
      tipoLocalNombre: u.tipos_local?.nombre,
    })),
  ];

  const filtered = searchRank(all, input, (s) => s.nombre);

  const handleSelect = (s: TipoLocalSuggestion) => {
    onChange(s);
    setInput(clearInputOnSelect ? "" : s.nombre);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative flex items-center">
        <Store
          className="absolute left-4 h-5 w-5 text-muted-foreground pointer-events-none z-10"
          aria-hidden
        />
        <PopoverTrigger asChild>
          <button
            ref={triggerRef}
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-label="Tipo de local"
            className={cn(
              "h-11 w-full rounded-2xl border-2 border-black bg-background pl-11 pr-4 text-left text-base outline-none focus:border-primary focus:ring-4 focus:ring-primary/15 transition",
              !value && "text-muted-foreground"
            )}
          >
            {value ? value.nombre : placeholder}
          </button>
        </PopoverTrigger>
      </div>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Escribe para buscar…"
            value={input}
            onValueChange={setInput}
          />
          <CommandList>
            <CommandEmpty>No se encontraron resultados.</CommandEmpty>
            {filtered.some((s) => s.kind === "tipo") && (
              <CommandGroup heading="Tipos de local">
                {filtered
                  .filter((s) => s.kind === "tipo")
                  .map((s) => (
                    <CommandItem
                      key={s.id}
                      value={`tipo-${s.id}`}
                      onSelect={() => handleSelect(s)}
                    >
                      {s.nombre}
                    </CommandItem>
                  ))}
              </CommandGroup>
            )}
            {filtered.some((s) => s.kind === "uso") && (
              <CommandGroup heading="Usos de local">
                {filtered
                  .filter((s) => s.kind === "uso")
                  .map((s) => (
                    <CommandItem
                      key={s.id}
                      value={`uso-${s.id}`}
                      onSelect={() => handleSelect(s)}
                    >
                      {s.nombre}
                      {(s as Extract<TipoLocalSuggestion, { kind: "uso" }>).tipoLocalNombre && (
                        <span className="ml-1 text-xs text-muted-foreground">
                          ({(s as Extract<TipoLocalSuggestion, { kind: "uso" }>).tipoLocalNombre})
                        </span>
                      )}
                    </CommandItem>
                  ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
